// @flow

import React from 'react';
import * as Immutable from 'immutable';
import InfiniteList from './InfiniteList';
import Lightbox from 'react-image-lightbox';
import type { ThreadMessage } from '../../types';

type Props = {
  isFetching: boolean,
  isFetchingPrevious: boolean,
  isReading: boolean,
  messages: Immutable.List<ThreadMessage>,
  lastReadIndex?: number,
  scrollPosition?: number,
  onRequestPreviousMessages: (scrollHeight: number) => void,
  onReadUpToIndex: (index: number) => void,
  onShowNewMessage: (value: boolean) => void,
};

type ImageLightboxState = {
  images: Array<string>,
  currentImageIndex: number,
};

type State = {
  currentLightbox?: ImageLightboxState,
};

const HD_RESOURCE_VARIANT: string = 'xxhdpi_3x';

const maxIndex = (list: Immutable.List<ThreadMessage>): number =>
  list.map(m => m.index).max();

export class List extends React.Component<Props, State> {
  props: Props;
  state: State;

  _list: HTMLDivElement;

  constructor(props: Props) {
    super(props);

    this.state = {
      showNewMessage: false,
    };
  }

  componentDidMount() {
    // Scrolling to last read index when mounting
    this.scrollToLastReadIndex(this.props.lastReadIndex);

    if (this._list != null) {
      this._list.addEventListener('scroll', this.onScroll);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.isFetching === false && this.props.isFetching === true) {
      // Scrolling to newest last read index once loaded
      this.scrollToLastReadIndex(nextProps.lastReadIndex);
    } else if (nextProps.lastReadIndex !== this.props.lastReadIndex) {
      // Scrolling to newest last read index when changed
      this.scrollToLastReadIndex(nextProps.lastReadIndex);
    } else {
      const previousLastIndex = maxIndex(this.props.messages);
      const newLastIndex = maxIndex(nextProps.messages);

      // Scrolling to bottom if new messages received
      if (
        this._list != null &&
        previousLastIndex != null &&
        newLastIndex != null &&
        previousLastIndex < newLastIndex
      ) {
        const { scrollTop, scrollHeight, offsetHeight } = this._list;

        // Checking is user is already on the bottom of the list to scroll to this message,
        // otherwise display new message popover
        if (offsetHeight + scrollTop >= scrollHeight) {
          this.scrollToLastReadIndex(newLastIndex);
        } else {
          // Notify new message should be displayed
          this.props.onShowNewMessage(true);
        }
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.scrollPosition !== this.props.scrollPosition) {
      const scrollDiff =
        this._list.scrollHeight - (this.props.scrollPosition || 0);

      this._list.scrollTop += scrollDiff;
    }
  }

  componentWillUnmount() {
    if (this._list != null) {
      this._list.removeEventListener('scroll', this.onScroll);
    }
  }

  scrollToLastReadIndex(index?: number) {
    // Scrolling to bottom for now (delay a bit to ensure UI is properly prepared)
    setTimeout(() => {
      if (this._list != null && index != null) {
        this._list.scrollTop = this._list.scrollHeight;
      }
    }, 0);

    const lastIndex = maxIndex(this.props.messages);
    if (lastIndex != null && index !== lastIndex) {
      // Marking thread as read
      this.props.onReadUpToIndex(Math.max(lastIndex, index || 0));
    }

    this.props.onShowNewMessage(false);
  }

  onScroll = event => {
    if (event.srcElement != null) {
      const { scrollTop, scrollHeight, offsetHeight } = event.srcElement;

      const scrollBottom = scrollHeight - (scrollTop + offsetHeight);
      const lastIndex = maxIndex(this.props.messages);
      if (scrollBottom <= 0 && lastIndex != null && !this.props.isReading) {
        this.props.onReadUpToIndex(lastIndex);
        this.props.onShowNewMessage(false);
      }
    }
  };

  onDisplayImages = (message: ThreadMessage, resource: BucketResource) => {
    const images = message.attachments
      .filter(attachment => attachment.resource.type.category === 'image')
      .map(attachment => attachment.resource.url[HD_RESOURCE_VARIANT]);

    if (images.length === 0) {
      return;
    }

    const currentImageIndex = images.findIndex(
      r => r === resource.url[HD_RESOURCE_VARIANT]
    );

    this.setState({
      currentLightbox: {
        images,
        currentImageIndex,
      },
    });
  };

  onMoveToImage = (index: number) =>
    this.setState(state => ({
      currentLightbox: {
        ...state.currentLightbox,
        currentImageIndex: index,
      },
    }));

  onHideImages = () =>
    this.setState({
      currentLightbox: undefined,
    });

  render() {
    const {
      isFetchingPrevious,
      messages,
      lastReadIndex,
      scrollPosition,
      onRequestPreviousMessages,
      onReadUpToIndex,
    } = this.props;

    const { currentLightbox } = this.state;

    return (
      <div ref={ref => (this._list = ref)} className="messages-container">
        <Images
          state={currentLightbox}
          onMove={this.onMoveToImage}
          onClose={this.onHideImages}
        />
        <InfiniteList
          list={this._list}
          messages={messages}
          lastReadIndex={lastReadIndex}
          isFetching={isFetchingPrevious}
          scrollPosition={scrollPosition}
          onRequestPreviousMessages={onRequestPreviousMessages}
          onDisplayMessage={this.onDisplayImages}
          onReadUpToIndex={onReadUpToIndex}
        />
      </div>
    );
  }
}

type ImagesProps = {
  state?: ImageLightboxState,
  onMove: (index: number) => void,
  onClose: () => void,
};

const Images = ({ state, onMove, onClose }: ImagesProps) => {
  if (state != null) {
    const { images, currentImageIndex: index } = state;

    const movePrevious = () =>
      onMove((index + images.length - 1) % images.length);
    const moveNext = () => onMove((index + 1) % images.length);

    return (
      <Lightbox
        mainSrc={images[index]}
        nextSrc={images[index + 1]}
        prevSrc={images[index - 1]}
        onMovePrevRequest={movePrevious}
        onMoveNextRequest={moveNext}
        onCloseRequest={onClose}
      />
    );
  } else {
    return null;
  }
};



// WEBPACK FOOTER //
// ./src/modules/messaging/components/messages/List.js